import * as storage from './storage.js';

export type InchPrecision = '1/16"' | '1/8"' | '1/4"';
export type LengthDisplayUnit = 'Inches' | 'Feet + inches' | 'Centimeters';

export interface FormatInchesOptions {
  precision?: InchPrecision;
  lengthUnit?: LengthDisplayUnit;
}

const DEFAULT_INCH_PRECISION: InchPrecision = '1/16"';
const DEFAULT_LENGTH_UNIT: LengthDisplayUnit = 'Inches';
const DOUBLE_PRIME = '\u2033';
const EM_DASH = '\u2014';
const PRECISION_DENOMINATORS: Record<InchPrecision, number> = {
  '1/16"': 16,
  '1/8"': 8,
  '1/4"': 4,
};

/**
 * Format a decimal inch value as an electrician-friendly string.
 *
 * Examples:
 *   formatInches(12)      -> "12″"
 *   formatInches(12.5)    -> "12-1/2″"
 *   formatInches(7.0625)  -> "7-1/16″"
 *   formatInches(0.4375)  -> "7/16″"
 *
 * By default, this respects Settings -> Length unit and Round to nearest.
 * Callers may pass precision/unit explicitly for deterministic exports/tests.
 */
export function formatInches(
  value: number,
  precisionOrOptions?: InchPrecision | FormatInchesOptions,
): string {
  if (!Number.isFinite(value)) return EM_DASH;
  const stored = getStoredFormatSettings();
  const precision =
    typeof precisionOrOptions === 'string'
      ? precisionOrOptions
      : (precisionOrOptions?.precision ?? stored.precision);
  const lengthUnit =
    typeof precisionOrOptions === 'object'
      ? (precisionOrOptions.lengthUnit ?? stored.lengthUnit)
      : stored.lengthUnit;

  if (lengthUnit === 'Centimeters') return `${formatCentimeters(value)} cm`;
  if (lengthUnit === 'Feet + inches') return formatFeetAndInches(value, precision);
  return formatInchesOnly(value, precision);
}

export function parseInchesInput(raw: string): number | null {
  let input = raw.trim().toLowerCase();
  if (!input) return null;

  input = input
    .replace(/\u2033|"/g, '')
    .replace(/\u2032|\u2019/g, "'")
    .replace(/\b(inches|inch|in)\b\.?/g, '')
    .trim();

  const sign = input.startsWith('-') ? -1 : 1;
  input = input.replace(/^[+-]/, '').trim();
  if (!input) return null;

  let feet = 0;
  const footMatch = input.match(/^(\d+(?:\.\d+)?)'\s*(.*)$/);
  if (footMatch) {
    feet = Number(footMatch[1]);
    input = footMatch[2].trim();
  }

  const inches = input ? parseInchRemainder(input) : 0;
  if (!Number.isFinite(feet) || inches === null) return null;
  return sign * (feet * 12 + inches);
}

function formatInchesOnly(value: number, precision: InchPrecision): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const denominator =
    PRECISION_DENOMINATORS[precision] ?? PRECISION_DENOMINATORS[DEFAULT_INCH_PRECISION];
  const fractionUnits = Math.round(abs * denominator);
  const whole = Math.floor(fractionUnits / denominator);
  const frac = fractionUnits - whole * denominator;
  if (frac === 0) return `${sign}${whole}${DOUBLE_PRIME}`;
  const [num, denom] = reduce(frac, denominator);
  if (whole === 0) return `${sign}${num}/${denom}${DOUBLE_PRIME}`;
  return `${sign}${whole}-${num}/${denom}${DOUBLE_PRIME}`;
}

function formatFeetAndInches(value: number, precision: InchPrecision): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  const denominator =
    PRECISION_DENOMINATORS[precision] ?? PRECISION_DENOMINATORS[DEFAULT_INCH_PRECISION];
  const fractionUnits = Math.round(abs * denominator);
  const unitsPerFoot = 12 * denominator;
  const feet = Math.floor(fractionUnits / unitsPerFoot);
  const remainingUnits = fractionUnits - feet * unitsPerFoot;
  const inches = remainingUnits / denominator;

  if (feet === 0) return formatInchesOnly(value, precision);
  if (remainingUnits === 0) return `${sign}${feet}' 0${DOUBLE_PRIME}`;
  return `${sign}${feet}' ${formatInchesOnly(inches, precision)}`;
}

function formatCentimeters(value: number): string {
  const centimeters = value * 2.54;
  const rounded = Math.round(centimeters * 10) / 10;
  if (Object.is(rounded, -0)) return '0.0';
  return rounded.toFixed(1);
}

function getStoredFormatSettings(): { precision: InchPrecision; lengthUnit: LengthDisplayUnit } {
  const settings = storage.get<{
    fractionPrecision?: unknown;
    lengthUnit?: unknown;
    roundToNearest?: unknown;
  }>('appSettings');
  return {
    precision:
      parseInchPrecision(settings?.fractionPrecision) ??
      parseInchPrecision(settings?.roundToNearest) ??
      DEFAULT_INCH_PRECISION,
    lengthUnit: parseLengthDisplayUnit(settings?.lengthUnit) ?? DEFAULT_LENGTH_UNIT,
  };
}

function parseInchPrecision(value: unknown): InchPrecision | undefined {
  if (value === '1/16"' || value === '1/8"' || value === '1/4"') return value;
  return undefined;
}

function parseLengthDisplayUnit(value: unknown): LengthDisplayUnit | undefined {
  if (value === 'Inches' || value === 'Feet + inches' || value === 'Centimeters') return value;
  return undefined;
}

function parseInchRemainder(input: string): number | null {
  const normalized = input.replace(/\s*-\s*/g, ' ').trim();
  const decimal = Number(normalized);
  if (Number.isFinite(decimal)) return decimal;

  const fraction = parseFraction(normalized);
  if (fraction !== null) return fraction;

  const mixedMatch = normalized.match(/^(\d+(?:\.\d+)?)\s+(\d+)\/(\d+)$/);
  if (!mixedMatch) return null;

  const whole = Number(mixedMatch[1]);
  const mixedFraction = parseFraction(`${mixedMatch[2]}/${mixedMatch[3]}`);
  if (!Number.isFinite(whole) || mixedFraction === null) return null;
  return whole + mixedFraction;
}

function parseFraction(value: string): number | null {
  const match = value.match(/^(\d+)\/(\d+)$/);
  if (!match) return null;

  const numerator = Number(match[1]);
  const denominator = Number(match[2]);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
    return null;
  }
  return numerator / denominator;
}

function reduce(num: number, denom: number): [number, number] {
  const g = gcd(num, denom);
  return [num / g, denom / g];
}

function gcd(a: number, b: number): number {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
}
